import React, { useState, useRef, useEffect, useCallback } from "react";
import { Box, IconButton, styled, TextField } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";

interface EditableCellProps {
  value: string | number | null;
  onSave: (newValue: string | number | null) => Promise<void>;
  type?: "text" | "number";
}

const Color = "#ff8038";

const HoverContainer = styled(Box)(() => ({
  position: "relative",
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
  "&:hover .edit-icon": {
    opacity: 1,
  },
}));

const EditingContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  position: "relative",
  width: "100%",
  background: "#fff",
  borderRadius: 4,
  boxShadow: "0 0 4px rgba(0,0,0,0.1)",
}));

const StyledEditIcon = styled(IconButton)({
  opacity: 0,
  position: "absolute",
  right: 0,
  padding: 4,
  transition: "opacity 0.2s",
});

const textFieldStyles = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    height: "auto",
    padding: "8px 0px",
    "&.Mui-focused fieldset": {
      borderColor: Color,
    },
    "&:hover fieldset": {
      borderColor: Color,
    },
  },
  "& .MuiOutlinedInput-input": {
    padding: "0px 4px",
    fontSize: "14px",
  },
};

export const EditableCell = React.memo(
  ({ value, onSave, type = "text" }: EditableCellProps) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value);
    const cellRef = useRef<HTMLDivElement>(null);

    const cancelEditing = useCallback(() => {
      setIsEditing(false);
      setEditValue(value);
    }, [value]);

    const handleSave = useCallback(async () => {
      try {
        await onSave(editValue);
      } catch (error) {
        console.error("Failed to save:", error);
      }
    }, [editValue, type, onSave]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        e.stopPropagation();
        if (isEditing) {
          if (e.key === "Enter") {
            e.preventDefault();
            setIsEditing(false);
            handleSave();
          } else if (e.key === "Escape") {
            cancelEditing();
          }
        }
      },
      [handleSave, cancelEditing]
    );

    useEffect(() => {
      if (!isEditing) return;

      const handleClickOutside = (event: MouseEvent) => {
        if (!cellRef.current?.contains(event.target as Node)) {
          setIsEditing(false);
          handleSave();
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, [isEditing, handleSave]);

    if (!isEditing) {
      return (
        <HoverContainer
          onDoubleClick={() => setIsEditing(true)}
          title={value?.toString() || ""}
        >
          {value?.toString() || ""}
          <StyledEditIcon
            className="edit-icon"
            size="small"
            onClick={() => setIsEditing(true)}
          >
            <EditIcon fontSize="small" sx={{ color: Color }} />
          </StyledEditIcon>
        </HoverContainer>
      );
    }

    return (
      <EditingContainer ref={cellRef}>
        <TextField
          size="small"
          value={editValue}
          type={type}
          onChange={(e) => setEditValue(e.target.value || null)}
          onKeyDown={handleKeyDown}
          autoFocus
          inputProps={{
            onWheel: (e) => e.target instanceof HTMLElement && e.target.blur(),
            style: { padding: "0px 4px" },
          }}
          sx={textFieldStyles}
        />
      </EditingContainer>
    );
  }
);
